<template>
    <transition :name="disableTransitions ? '' : 'fade'" @after-leave="$emit('hidden')">
        <div class="dialog" ref="dialog" v-show="show">
            <div class="wrapper" @click.self="closeOnClickOutside ? close() : () => {}">
                <div style="flex-grow: 1"></div>
                <transition :name="disableTransitions ? '' : 'slide-down'">
                    <div
                        class="container"
                        v-show="show"
                        :style="containerStyles"
                        role="dialog"
                        aria-modal="true"
                        :aria-labelledby="$slots.header ? headerId : null"
                    >
                        <div class="icon-wrapper" v-if="!hideIcon && icon" :style="{ paddingTop: innerPadding }">
                            <div class="circle" :style="{ backgroundColor: secondaryColor }">
                                <div class="icon" :class="icon" :style="{ color: colorMain }"></div>
                            </div>
                        </div>
                        <div class="header" :id="headerId">
                            <slot name="header"></slot>
                        </div>
                        <div class="body" :style="bodyStyles">
                            <slot name="body" :close="close"></slot>
                        </div>
                        <div class="footer" v-if="showFooter">
                            <slot name="footer" ref="footer" v-bind:close="close">
                                <button class="button" @click="close" :style="{ backgroundColor: colorMain }">
                                    Close
                                </button>
                            </slot>
                        </div>
                        <div class="close-x" v-if="showX">
                            <button @click="close" aria-label="Close">
                                <i class="far fa-window-close"></i>
                            </button>
                        </div>
                        <transition :name="initialLoad ? '' : 'fade'">
                            <div class="loading fast" v-show="$store.getters['dialog/loading']">
                                <loading-spinner />
                            </div>
                        </transition>
                    </div>
                </transition>
                <div style="flex-grow: 3"></div>
            </div>
        </div>
    </transition>
</template>

<script>
    const tinycolor = require('tinycolor2');
    import { mapGetters } from 'vuex';
    import { constants, dom, strings } from '../../utils';

    export default {
        name: 'DialogWindow',
        data() {
            return {
                initialLoad: true,
                previousFocus: null,
                headerId: `dialog_header_${strings.random()}`,
            };
        },
        props: {
            show: {
                type: Boolean,
                required: true,
            },
            width: {
                type: String,
                default: '400px',
            },
            height: {
                type: String,
                default: 'auto',
            },
            overflow: {
                type: String,
                default: 'auto',
            },
            closeOnClickOutside: {
                type: Boolean,
                default: false,
            },
            closeOnEscape: {
                type: Boolean,
                default: false,
            },
            innerPadding: {
                type: String,
                default: '1em',
            },
            flex: {
                type: Boolean,
                default: false,
            },
            showFooter: {
                type: Boolean,
                default: true,
            },
            showX: {
                type: Boolean,
                default: false,
            },
            hideIcon: {
                type: Boolean,
                default: false,
            },
            defaultFocusId: {
                type: String,
                default: '',
            },
            color: {
                type: String,
                default: '',
            },
            icon: {
                type: String,
                default: '',
            },
            skipFocus: {
                type: Boolean,
                default: false,
            },
            background: {
                type: String,
                default : '',
            }
        },
        computed: {
            ...mapGetters({
                variables: 'layout/variables',
                disableTransitions: 'dialog/disableTransitions',
            }),
            variables() {
                return {};
            },
            containerStyles() {
                return {
                    overflow: this.overflow,
                    width: this.width,
                    height: this.height,
                    paddingTop: this.innerPadding,
                    paddingBottom: this.innerPadding,
                    paddingRight: this.innerPadding,
                    paddingLeft: !this.hideIcon && this.icon ? '75px' : this.innerPadding,
                    display: this.flex ? 'flex' : '',
                    background: this.background || '',
                };
            },
            bodyStyles() {
                return {
                    'overflow-y': this.flex ? 'auto' : '',
                };
            },
            secondaryColor() {
                return tinycolor(this.colorMain).setAlpha(0.1).toRgbString();
            },
            colorMain() {
                return this.color ? this.color : this.variables.colorMain;
            },
        },
        methods: {
            close() {
                this.$emit('closed');
            },
            onKeydown(e) {
                if ((this.closeOnEscape || this.showX) && e.which === constants.KEY_ESCAPE) {
                    this.close();
                } else if (e.which === constants.KEY_TAB) {
                    // Trap keyboard tab focus within this dialog
                    let elems = dom.focusableElements(this.$refs.dialog);
                    if (e.shiftKey) {
                        if (e.target === elems[0]) {
                            e.preventDefault();
                            elems[elems.length - 1].focus();
                        }
                    } else {
                        if (e.target === elems[elems.length - 1]) {
                            e.preventDefault();
                            elems[0].focus();
                        }
                    }
                }
            },
        },
        watch: {
            async show(show) {
                this.$store.commit('page/dialogOpen', show);
                if (show) {
                    setTimeout(() => {
                        // using $nextTick sometimes doesn't work for focus
                        if (!this.skipFocus) {
                            this.previousFocus = document.activeElement;
                            let elem;
                            if (this.defaultFocusId) {
                                elem = document.getElementById(this.defaultFocusId);
                            } else {
                                elem = dom.focusableElements(this.$refs.dialog)[0];
                            }
                            if (elem) elem.focus();
                        }

                        this.initialLoad = true;
                        document.addEventListener('keydown', this.onKeydown);
                    });
                } else {
                    document.removeEventListener('keydown', this.onKeydown);
                    if (this.previousFocus) {
                        setTimeout(() => {
                            this.previousFocus.focus();
                            this.previousFocus = null;
                        });
                    }
                }
            },
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.onKeydown);
        },
    };
</script>

<style lang="scss" scoped>
    .loading {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background: $color-white;
        z-index: 10;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding-bottom: 250px;

        .message {
            font-size: 1.1rem;
            margin-top: 1em;
        }
    }

    .dialog {
        position: fixed;
        z-index: 1006;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        opacity: 1;
    }

    .wrapper {
        background: $color-modal-blocker;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        height: 100%;
        padding: 15px;
    }

    .container {
        background: $color-white;
        overflow-y: auto;
        max-width: 95%;
        position: relative;
        max-height: 100%;
        display: flex;
        flex-direction: column;

        @include border;
        @include box-shadow-large;
        @include border-radius-small;
    }

    .close-x {
        position: absolute;
        right: 0;
        top: 0;
        font-size: 1.4em;
        padding: 6px;

        /deep/ button {
            z-index: 11;
            color: $color-label;
            border: none;
            border-radius: 0;
            padding: 0;
            font-size: inherit;

            &:hover {
                color: $color-black;
            }
        }
    }

    .icon-wrapper {
        width: 75px;
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        justify-content: center;

        .circle {
            width: 2.5rem;
            height: 2.5rem;
            justify-content: center;
            display: flex;
            align-items: center;
            border-radius: 50%;
            font-size: 1.1rem;
        }
    }

    .header {
        @extend h3;
        margin-top: 0;

        &:empty {
            display: none;
        }
    }

    .body {
        text-align: left;
        flex-grow: 1;
    }

    .footer {
        @include button-group;
        margin-top: 1.25em;
        text-align: right;
        flex-shrink: 0;
        justify-content: flex-end;

        &:empty {
            display: none;
        }

        /* Fixes Vue bug https://github.com/vuejs/vue-loader/issues/820 */
        /deep/ button {
            margin-left: 5px;

            &:first-child {
                margin-left: 0;
            }
        }
    }
</style>